import { FC, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { comparisonSheetSuppliersSelector } from '@Features/MaterialRequest/requests/MaterialRequisition/store/selectors/materialRequestSelector';
import { useAppDispatch, useAppSelector } from '@Hooks/index';

import { stateOpenApproveModal } from '@Store/selectors/approvalsListSelector';
import { approvalsListActions } from '@Store/slices/approvalsListSlice';
import { useFormik, useFormikContext } from 'formik';
import TenderApproveRequest from '@Types/api/form/tenderRequest/TenderApproveRequest';
import { NotificationContext } from '@Context/NotificationContext';
import { ApprovalActions } from '@Enum/ApprovalActions';
import {
  useApproveTenderMutation,
  useGetTenderTotalAmountQuery,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';

import { composeSelectedOffers } from '@Features/MaterialRequest/requests/components/ComparisonSheet/utils';
import { ComparisonSheetState } from '@Features/MaterialRequest/requests/components/ComparisonSheet/ComparisonSheet.types';
import { NoData } from '@Components/RequestList/noData';
import { ViewResolver } from '@Components/ViewResolver';
import { initFormikValuesType } from './initFormikValues';

import {
  REF_CODE,
  REMARKS,
  IS_COMPLY,
  CM_OPTION,
  PRIORITY_ID,
} from '../../../utils/const';

import ApproveTabDesktop from './desktop/ApproveTabDesktop';
import ApproveTabMobile from './mobile/ApproveTabMobile';
import ApproveModal from './components/ApproveModal/ApproveModal';

interface ApproveTabProps {}
const ApproveTab: FC<ApproveTabProps> = () => {
  const { taskId, tenderId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const stateOpenModal = useAppSelector(stateOpenApproveModal);
  const comparisonSheetSuppliers = useAppSelector(
    comparisonSheetSuppliersSelector
  );
  const { data: totalAmountData } = useGetTenderTotalAmountQuery(tenderId!, {
    skip: !tenderId,
  });

  const comparisonSheetFormik = useFormikContext<ComparisonSheetState>();

  const selectedSheetOffers = useMemo(() => {
    if (
      comparisonSheetFormik.values.allOffers &&
      comparisonSheetFormik.values.selectedOffersIDs
    ) {
      return composeSelectedOffers(
        comparisonSheetFormik.values.allOffers,
        comparisonSheetFormik.values.selectedOffersIDs
      );
    }
    return [];
  }, [
    comparisonSheetFormik.values.allOffers,
    comparisonSheetFormik.values.selectedOffersIDs,
  ]);

  const {
    taskApprovedNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);

  const [approveTender] = useApproveTenderMutation();

  const approveFormik = useFormik({
    initialValues: initFormikValuesType,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const payload = useMemo(() => {
    const { comment, approver } = approveFormik.values;
    const tenderDataList = selectedSheetOffers
      ?.map(({ material: { matId }, vendorOffers }) => {
        const offersForMaterial = vendorOffers.map(
          ({ tenderItemId, vendorId, rate, freeItem }) => ({
            reqItemId: Number(tenderItemId),
            matId: matId!,
            refCode: REF_CODE,
            vendorId: Number(vendorId),
            unitRate: rate,
            // already selected
            itemRec: true,
            freeItem: freeItem!,
            remarks: REMARKS,
            isComply: IS_COMPLY,
          })
        );
        return offersForMaterial;
      })
      .flat();
    return {
      comment,
      decision: ApprovalActions.APPROVE,
      purchaseOfficerId: 0,
      comparisonDto: {
        cmOption: CM_OPTION,
        priorityId: PRIORITY_ID,
        tenderDataList,
      },
      ...((approver && { selectedApproverUserId: Number(approver) }) || {}),
      taskId,
    } as TenderApproveRequest;
  }, [approveFormik.values, selectedSheetOffers, taskId]);

  const handleApproveModal = useCallback((): void => {
    approveTender(payload)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        dispatch(approvalsListActions.setOpenModalApprove(false));
        navigate('../../');
      })
      .catch(({ data: error }) => {
        errorNotification(error);
      });
  }, [
    approveTender,
    dispatch,
    navigate,
    payload,
    taskApprovedNotification,
    errorNotification,
  ]);

  const handleReject = useCallback((): void => {
    approveTender({ ...payload, decision: 'Reject' })
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        dispatch(approvalsListActions.setOpenModalApprove(false));
        navigate('../../');
      })
      .catch(({ data: error }) => {
        errorNotification(error);
      });
  }, [
    approveTender,
    dispatch,
    navigate,
    payload,
    taskRejectedNotification,
    errorNotification,
  ]);

  const handleOpenModal = (): void => {
    dispatch(approvalsListActions.setOpenModalApprove(true));
  };

  const handleCancel = (): void => {
    dispatch(approvalsListActions.setOpenModalApprove(false));
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  if (!selectedSheetOffers || !comparisonSheetSuppliers) {
    return <NoData context="comparison-screen" />;
  }

  return (
    <>
      <ViewResolver
        desktop={
          <ApproveTabDesktop
            comparisonSheetSuppliers={comparisonSheetSuppliers}
            handleOpenModal={handleOpenModal}
            handleBack={handleBack}
            selectedSheetOffers={selectedSheetOffers}
            totalAmountData={totalAmountData}
          />
        }
        mobile={
          <ApproveTabMobile
            comparisonSheetSuppliers={comparisonSheetSuppliers}
            handleOpenModal={handleOpenModal}
            handleBack={handleBack}
            selectedSheetOffers={selectedSheetOffers}
            totalAmountData={totalAmountData}
          />
        }
      />
      <ApproveModal
        isModalOpen={stateOpenModal}
        handleApprove={handleApproveModal}
        handleReject={handleReject}
        formik={approveFormik}
        handleCancel={handleCancel}
        hideApproverSelection={Boolean(
          comparisonSheetFormik.values.disabledItems?.disableApprover
        )}
      />
    </>
  );
};

export default ApproveTab;
